import { StrictMode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import './styles/tokens.css';
import './styles/fonts.css';
import './styles/app.css';

import { App } from './app';
import { AuthProvider, useAuth } from './auth/auth-context';
import { AppProvider, ResumingProvider } from './api/app-context';
import { createApi, fetchMe } from './api/client';
import { shouldRetryRead, loadErrorLine } from './api/errors';
import { PAGE_POLL_MS, qk } from './api/keys';
import { OrgScope } from './api/org-scope';
import { BareState } from './components/ui/bare-state';
import { DevSwitch } from './components/ui/dev-switch';
import { Loading } from './components/ui/loading';
import { chooseOrg, forgetOrg, readStoredOrg, rememberOrg } from './lib/org-choice';
import { initErrorReporting, tagReportScope } from './observability';

/**
 * Bootstrap order (ui-design §6.1): auth provider → org resolve → router.
 *
 * `GET /me` is the one bootstrap read: the caller's name for the nav foot and
 * the orgs for the context. An empty `orgs[]` is a legitimate first login for an
 * operator account (Auth0 admin permission, no membership row) — it renders a
 * plain empty state, never a crash or a redirect loop.
 *
 * That read is also the only place membership *ending* shows: org-scoped routes
 * 404 rather than 403 for a non-member, so removal is unreadable in responses.
 * `/me` re-polls on the page interval and on focus, so a remembered org the
 * reader is no longer in falls back within one poll — to their first remaining
 * org, or, if none remain, to the empty state below.
 */
function Bootstrap() {
  const { fetchWithAuth } = useAuth();
  const [preferredId, setPreferredId] = useState<string | null>(readStoredOrg);

  const me = useQuery({
    queryKey: qk.me(),
    queryFn: () => fetchMe(fetchWithAuth),
    refetchInterval: PAGE_POLL_MS,
  });

  // The org the router subtree is currently scoped to, held here because
  // `Bootstrap` is the one component that never unmounts. Every early return
  // below tears the subtree down, so `OrgScope` cannot remember this itself —
  // see the note there.
  const scopedOrg = useRef<string | null>(null);

  const orgs = me.data?.orgs;
  const choice = chooseOrg(orgs ?? [], preferredId);
  // Only a resolved read can invalidate a preference — the empty list standing
  // in for a pending one is not evidence of anything.
  const stale = orgs ? choice.stale : false;
  const org = orgs ? choice.org : null;

  // Forgetting is a write, so it waits for commit: `chooseOrg` already returned
  // the org to run under, and render stays free of side effects.
  useEffect(() => {
    if (!stale) return;
    forgetOrg();
    setPreferredId(null);
  }, [stale]);

  const userId = me.data?.user.id;
  useEffect(() => tagReportScope(userId, org?.id), [userId, org?.id]);

  const switchOrg = useCallback(
    (orgId: string) => {
      if (!orgs?.some((entry) => entry.id === orgId)) return;
      rememberOrg(orgId);
      setPreferredId(orgId);
    },
    [orgs],
  );

  // One api per org: it closes over the org id, and rebuilding it on every
  // render would hand a new `api` to every consumer 60s at a time.
  const orgId = org?.id;
  const api = useMemo(
    () => (orgId ? createApi(fetchWithAuth, orgId) : null),
    [fetchWithAuth, orgId],
  );

  if (me.isPending) {
    return (
      <BareState>
        <div className="line">
          <Loading immediate />
        </div>
      </BareState>
    );
  }

  if (me.isError) {
    return (
      <BareState>
        <div className="line">The server did not answer.</div>
        <div className="quiet">{loadErrorLine(me.error)}</div>
        <DevSwitch />
      </BareState>
    );
  }

  if (!org || !api) {
    return (
      <BareState>
        <div className="line">No club is linked to this account yet.</div>
        <div className="quiet">
          Signed in as {me.data.user.name ?? me.data.user.email}. This account has no club
          membership.
        </div>
        <DevSwitch />
      </BareState>
    );
  }

  return (
    <AppProvider api={api} org={org} me={me.data} switchOrg={switchOrg}>
      <ResumingProvider>
        <BrowserRouter>
          <OrgScope scoped={scopedOrg} />
          <App />
        </BrowserRouter>
      </ResumingProvider>
    </AppProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Window focus is half of the freshness policy (ui-design §4.1/§4.3); the
      // other half is the per-query interval.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 0,
      // One retry for network failures and 5xx, none for a 4xx — see shouldRetryRead.
      retry: shouldRetryRead,
      // The dashboard talks to exactly one origin, its own. The browser's
      // online flag is not a useful signal about that server, and letting it
      // pause a read leaves a surface hanging with nothing to read instead of
      // saying the server did not answer.
      networkMode: 'always',
    },
    mutations: {
      retry: 0,
      networkMode: 'always',
    },
  },
});

initErrorReporting();

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Bootstrap />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
